import { useEffect, useRef, useState } from "react";
import { searchUsers } from "@/lib/api";
import { sfx } from "@/lib/sounds";

/**
 * MentionAutocomplete — hooks a textarea and detects `@` triggers.
 * When the user types `@abc`, it shows a dropdown of matching users from /users/search.
 * Clicking a user (or pressing Enter/Tab) inserts `@username ` at the caret.
 *
 * Usage:
 *   const ref = useRef();
 *   <textarea ref={ref} value={body} onChange={...} />
 *   <MentionAutocomplete inputRef={ref} value={body} onInsert={(nextText) => setBody(nextText)} />
 */
export default function MentionAutocomplete({ inputRef, value, onInsert }) {
  const [query, setQuery] = useState(null); // current @query or null
  const [caret, setCaret] = useState(0);
  const [triggerStart, setTriggerStart] = useState(-1);
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  // Watch caret + value → find active @token before caret
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const detect = () => {
      const pos = el.selectionStart ?? 0;
      const before = value.slice(0, pos);
      // find last @ that starts a new token (start of string or after whitespace)
      const m = before.match(/(?:^|\s)@([a-zA-Z0-9_.\-]{0,32})$/);
      if (m) {
        setTriggerStart(pos - m[1].length - 1); // index of `@`
        setQuery(m[1]);
        setCaret(pos);
        setActive(0);
      } else {
        setQuery(null); setResults([]);
      }
    };
    detect();
    const handle = () => detect();
    el.addEventListener("keyup", handle);
    el.addEventListener("click", handle);
    return () => {
      el.removeEventListener("keyup", handle);
      el.removeEventListener("click", handle);
    };
  }, [value, inputRef]);

  // Debounced search
  useEffect(() => {
    if (query === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (query.trim().length === 0) {
        // Show a few suggestions? For now, empty.
        setResults([]);
        return;
      }
      try {
        const users = await searchUsers(query);
        setResults(users.slice(0, 6));
      } catch { setResults([]); }
    }, 180);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const insert = (username) => {
    if (triggerStart < 0) return;
    const before = value.slice(0, triggerStart);
    const after = value.slice(caret);
    const next = `${before}@${username} ${after.startsWith(" ") ? after.trimStart() : after}`;
    onInsert(next);
    setQuery(null); setResults([]);
    // Move caret after inserted mention
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      const pos = (before + `@${username} `).length;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
    sfx.click();
  };

  // Keyboard nav
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (!results.length || query === null) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % results.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + results.length) % results.length); }
      else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insert(results[active].mc_username || results[active].name);
      } else if (e.key === "Escape") {
        setQuery(null); setResults([]);
      }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [results, active, query, value]); // eslint-disable-line

  if (query === null || results.length === 0) return null;

  return (
    <div
      className="absolute z-30 mt-1 min-w-[260px] rounded-xl glass border border-white/10 backdrop-blur-xl overflow-hidden shadow-xl"
      data-testid="mention-autocomplete"
    >
      <div className="text-[10px] uppercase tracking-widest text-white/40 px-3 pt-2 pb-1">Mention someone</div>
      {results.map((u, i) => (
        <button
          key={u.id}
          type="button"
          onMouseEnter={() => setActive(i)}
          onClick={() => insert(u.mc_username || u.name)}
          data-testid={`mention-item-${i}`}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
            i === active ? "bg-[#FFBF00]/15 text-[#FFBF00]" : "hover:bg-white/5"
          }`}
        >
          <img src={u.avatar} alt="" className="w-7 h-7 rounded-md" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{u.name}</div>
            {u.mc_username && <div className="text-[10px] font-mono text-white/40 truncate">@{u.mc_username}</div>}
          </div>
          {u.is_creator && <span className="text-[9px] uppercase tracking-widest text-[#FFBF00]">Creator</span>}
        </button>
      ))}
    </div>
  );
}
